import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Workflow as WorkflowIcon } from "lucide-react";
import ClustrLogo from "@/components/icons/ClustrLogo";
import Footer from "@/components/landing/Footer";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

const Workflow = () => {
  const { title, description, image } = APP_METADATA.workflow;
  useMetadata({ title, description, image });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="relative">
        <div className="w-full">
          <img 
            src="/lovable-uploads/0cdaa5a9-9c78-49ac-b71f-661d707c4f81.png"
            alt="Clustr Workflow Visualization" 
            className="w-full object-contain"
          />
        </div>
        
        <div className="absolute top-4 left-0 w-full px-6">
          <div className="max-w-6xl mx-auto">
            <Link to="/">
              <ClustrLogo 
                width={180} 
                height={54} 
                src="/lovable-uploads/abf9b449-c10d-4d99-b03c-c106652be5bb.png"
              />
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full px-6 pb-6 bg-gradient-to-t from-white via-white/80 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-5">
              <WorkflowIcon className="h-20 w-20 text-clustr-blue" />
              <h1 className="text-6xl font-bold text-clustr-text">Workflow Guide</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">The Clustr Workflow</h2>
            <p>
              Learn how to integrate Clustr into your research and creative workflow for maximum productivity. 
              Our workflow is designed to help you capture, organize, and utilize digital inspiration efficiently.
            </p>

            <div className="relative py-10">
              <div className="space-y-16">
                {/* Step 1: Capture */}
                <div className="flex gap-6">
                  <div className="w-16 h-16 rounded-full bg-clustr-blue/10 flex items-center justify-center text-clustr-blue font-bold text-2xl flex-shrink-0">1</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Capture</h3>
                    <p className="text-gray-700">
                      Start by capturing content from anywhere on the web:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-2">
                      <li>Use the browser extension to save content with one click</li>
                      <li>Automatically extract key information and thumbnails</li>
                      <li>Save to your Inbox for later processing</li>
                    </ul>
                  </div>
                </div>

                {/* Step 2: Organize */}
                <div className="flex gap-6">
                  <div className="w-16 h-16 rounded-full bg-clustr-blue/10 flex items-center justify-center text-clustr-blue font-bold text-2xl flex-shrink-0">2</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Organize</h3>
                    <p className="text-gray-700">
                      Create structure and meaning with thoughtful organization:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-2">
                      <li>Create themed collections for different projects or interests</li>
                      <li>Add tags to make content discoverable</li>
                      <li>Write notes and descriptions to add context</li>
                    </ul>
                  </div>
                </div>

                {/* Step 3: Connect */}
                <div className="flex gap-6">
                  <div className="w-16 h-16 rounded-full bg-clustr-blue/10 flex items-center justify-center text-clustr-blue font-bold text-2xl flex-shrink-0">3</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Connect</h3>
                    <p className="text-gray-700">
                      Build relationships between your content:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-2">
                      <li>Create visual boards to connect related items</li>
                      <li>Use the network view to discover connections</li>
                      <li>Cross-reference between collections</li>
                    </ul>
                  </div>
                </div>

                {/* Step 4: Use */}
                <div className="flex gap-6">
                  <div className="w-16 h-16 rounded-full bg-clustr-blue/10 flex items-center justify-center text-clustr-blue font-bold text-2xl flex-shrink-0">4</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Use</h3>
                    <p className="text-gray-700">
                      Put your curated content to work:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-2">
                      <li>Quick access to your research when needed</li>
                      <li>Export collections for presentations</li>
                      <li>Share insights with your team</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="my-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-4">Pro Tips</h3>
              <ul className="list-disc pl-6 space-y-3">
                <li>Schedule regular time to process your Inbox</li>
                <li>Use consistent naming conventions for collections and tags</li>
                <li>Review and refine your organizational structure periodically</li>
                <li>Take advantage of both visual and list views for different tasks</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Workflow;
